import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import StandardsSection from '../components/StandardsSection';
import {
  FaArrowRight,
  FaCertificate,
  FaCheckCircle,
  FaClipboardCheck,
  FaExternalLinkAlt,
  FaShieldAlt,
  FaGraduationCap,
  FaHardHat,
  FaBullseye,
  FaLightbulb,
  FaUsers,
  FaBuilding,
  FaTools,
  FaTimes,
  FaSearch,
} from 'react-icons/fa';

import '../styles/about-page.css';
import aboutHeroImage from '../pics/Aboutpageimage12.png';

const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AboutPage() {
  const navigate = useNavigate();
  const [certificationImage, setCertificationImage] = useState(null);
  const [loadingCert, setLoadingCert] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchCertificationImage();
  }, []);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setShowCertModal(false);
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const fetchCertificationImage = async () => {
    try {
      setLoadingCert(true);

      let companyId = localStorage.getItem('companyId');

      if (!companyId) {
        try {
          const companiesResponse = await axios.get(`${API_URL}/companies`);

          if (
            companiesResponse.data.success &&
            companiesResponse.data.companies?.length > 0
          ) {
            companyId = companiesResponse.data.companies[0]._id;
            localStorage.setItem('companyId', companyId);
          }
        } catch (companiesErr) {
          console.log(
            'Could not fetch companies list:',
            companiesErr.message
          );
        }
      }

      if (companyId) {
        const response = await axios.get(
          `${API_URL}/admin/certification/${companyId}`
        );

        if (response.data.success) {
          setCertificationImage(
            response.data.data.certificationImageUrl
          );
        }
      }
    } catch (err) {
      console.log(
        'No certification image available:',
        err.message
      );
    } finally {
      setLoadingCert(false);
    }
  };

  const scrollToSection = (id, tab) => {
    setActiveTab(tab);

    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <div className="about-page">
      <SEO
        title="About Us"
        description="Learn about Dofcan Oilserve Limited, our 15+ years of experience delivering safety-focused lifting, inspection and certification support to the oil and gas industry."
        path="/about"
      />
      {/* =====================================================
          TOP ABOUT HEADER
      ====================================================== */}
      <section className="about-top-section">
        <div className="about-container about-top-inner">

          {/* Status / Badge Row */}
          <div className="about-badge-row">
            <span className="about-badge about-badge-primary">
              <span className="about-status-dot"></span>
              Safety &amp; Operational Excellence
            </span>

            <span className="about-badge about-badge-light">
              <FaCheckCircle />
              Training • Inspection • Certification
            </span>

            <span className="about-reference-id">
              DOFCAN OILSERVE LIMITED
            </span>
          </div>

          {/* Main Heading */}
          <div className="about-main-grid">
            <div className="about-heading-area">
              <h1>
                Building Safer,
                <br />
                More Competent
                <br />
                Lifting Operations
              </h1>

              <p>
                Dofcan Oilserve Limited is committed to advancing
                safety, competence, and operational confidence
                across the lifting industry through rigorous
                training, inspection, and certification services.
              </p>
            </div>

            {/* Image / Company Authority Card */}
            <div className="about-hero-card">
              <div className="about-hero-image-wrap">
                <img
                  src={aboutHeroImage}
                  alt="Dofcan Oilserve industrial operations"
                />

                <div className="about-image-overlay"></div>

                <div className="about-image-label">
                  <FaShieldAlt />
                  <div>
                    <span>Our Commitment</span>
                    <strong>Safety First</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="about-stat-grid">
            <div className="about-stat-card">
              <div className="about-stat-top">
                <span>Experience</span>
                <FaBuilding />
              </div>

              <div className="about-stat-number">15+</div>

              <div className="about-stat-label">
                Years of Industry Experience
              </div>
            </div>

            <div className="about-stat-card">
              <div className="about-stat-top">
                <span>Professionals</span>
                <FaUsers />
              </div>

              <div className="about-stat-number">3k+</div>

              <div className="about-stat-label">
                Professionals Trained
              </div>
            </div>

            <div className="about-stat-card">
              <div className="about-stat-top">
                <span>Companies</span>
                <FaBuilding />
              </div>

              <div className="about-stat-number">500+</div>

              <div className="about-stat-label">
                Companies Served
              </div>
            </div>

            <div className="about-stat-card">
              <div className="about-stat-top">
                <span>Core Areas</span>
                <FaTools />
              </div>

              <div className="about-stat-number">3</div>

              <div className="about-stat-label">
                Training, Inspection &amp; Certification
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="about-tabs">
            <button
              className={activeTab === 'overview' ? 'active' : ''}
              onClick={() =>
                scrollToSection('company-overview', 'overview')
              }
            >
              <FaSearch />
              Overview
            </button>

            <button
              className={activeTab === 'mission' ? 'active' : ''}
              onClick={() =>
                scrollToSection('mission-vision', 'mission')
              }
            >
              <FaBullseye />
              Mission &amp; Vision
            </button>

            <button
              className={activeTab === 'services' ? 'active' : ''}
              onClick={() =>
                scrollToSection('core-services', 'services')
              }
            >
              <FaTools />
              Core Services
            </button>

            <button
              className={activeTab === 'credentials' ? 'active' : ''}
              onClick={() =>
                scrollToSection('credentials', 'credentials')
              }
            >
              <FaCertificate />
              Credentials
            </button>
          </div>
        </div>
      </section>

      {/* =====================================================
          COMPANY OVERVIEW
      ====================================================== */}
      <section
        id="company-overview"
        className="about-overview-section"
        data-reveal
      >
        <div className="about-container">
          <div className="about-section-heading">
            <div>
              <div className="about-eyebrow">
                <span></span>
                Who We Are
              </div>

              <h2>Company Overview</h2>
            </div>

            <p>
              We focus on practical competence, operational safety,
              and dependable professional services for the lifting
              and industrial environment.
            </p>
          </div>

          <div className="about-overview-grid" data-reveal="stagger">
            <article className="about-overview-card about-overview-main">
              <div className="about-card-icon">
                <FaHardHat />
              </div>

              <div>
                <span className="about-card-label">
                  OUR APPROACH
                </span>

                <h3>
                  Practical expertise with safety at the centre
                </h3>

                <p className="ap-text">
                  Our approach is built around developing capable
                  professionals, supporting safe equipment
                  operations, and providing certification that
                  gives organisations greater confidence in their
                  people and processes.
                </p>

                <p>
                  We believe effective safety performance comes
                  from combining technical knowledge, practical
                  training, proper inspection, and continuous
                  commitment to professional standards.
                </p>
              </div>
            </article>

            <article className="about-overview-card">
              <div className="about-card-icon">
                <FaGraduationCap />
              </div>

              <span className="about-card-label">
                PEOPLE
              </span>

              <h3>Developing Competence</h3>

              <p>
                We help professionals develop the knowledge and
                practical competence required to perform their
                responsibilities safely and effectively.
              </p>
            </article>

            <article className="about-overview-card">
              <div className="about-card-icon">
                <FaClipboardCheck />
              </div>

              <span className="about-card-label">
                ASSURANCE
              </span>

              <h3>Operational Confidence</h3>

              <p>
                Through structured inspection, training, and
                certification processes, we support organisations
                in maintaining confidence in their operations.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          MISSION / VISION
      ====================================================== */}
      <section
        id="mission-vision"
        className="about-foundation-section"
        data-reveal
      >
        <div className="about-container">
          <div className="about-section-heading">
            <div>
              <div className="about-eyebrow about-eyebrow-blue">
                <span></span>
                Our Foundation
              </div>

              <h2>Mission &amp; Vision</h2>
            </div>

            <p>
              The principles that guide our work and shape the
              value we provide to clients and professionals.
            </p>
          </div>

          <div className="about-foundation-grid">
            <article className="about-foundation-card">
              <div className="about-foundation-number">01</div>

              <div className="about-foundation-icon">
                <FaBullseye />
              </div>

              <span className="about-card-label">
                OUR MISSION
              </span>

              <h3>Advancing Safety &amp; Competence</h3>

              <p>
                To advance safety, competence, and operational
                confidence across the lifting industry through
                rigorous training, inspection, and certification
                services.
              </p>
            </article>

            <article className="about-foundation-card">
              <div className="about-foundation-number">02</div>

              <div className="about-foundation-icon">
                <FaLightbulb />
              </div>

              <span className="about-card-label">
                OUR VISION
              </span>

              <h3>A Leading African Reference Point</h3>

              <p>
                To be a leading African reference point for lifting
                operations training, equipment inspection, and
                professional certification.
              </p>
            </article>

            <article className="about-foundation-card">
              <div className="about-foundation-number">03</div>

              <div className="about-foundation-icon">
                <FaShieldAlt />
              </div>

              <span className="about-card-label">
                OUR COMMITMENT
              </span>

              <h3>Safety Through Professional Practice</h3>

              <p>
                We remain focused on practical learning,
                responsible inspection, professional certification,
                and creating safer working environments.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          CORE SERVICES
      ====================================================== */}
      <section
        id="core-services"
        className="about-services-section"
      >
        <div className="about-container">
          <div className="about-section-heading">
            <div>
              <div className="about-eyebrow">
                <span></span>
                Areas of Focus
              </div>

              <h2>Core Services</h2>
            </div>

            <p>
              Our core service areas are designed around the
              development of competent people and safer industrial
              operations.
            </p>
          </div>

          <div className="about-services-grid">
            <article className="about-service-card">
              <div className="about-service-icon">
                <FaGraduationCap />
              </div>

              <div className="about-service-content">
                <span>01 • TRAINING</span>

                <h3>Professional Training</h3>

                <p>
                  Structured training designed to build practical
                  knowledge, competence, and confidence for
                  professionals working within lifting and
                  industrial operations.
                </p>
              </div>

              <div className="about-service-footer">
                <strong>Focus</strong>
                <span>Competence Development</span>
              </div>
            </article>

            <article className="about-service-card">
              <div className="about-service-icon">
                <FaClipboardCheck />
              </div>

              <div className="about-service-content">
                <span>02 • INSPECTION</span>

                <h3>Equipment Inspection</h3>

                <p>
                  Inspection services supporting the safe use,
                  operational confidence, and proper management of
                  lifting and industrial equipment.
                </p>
              </div>

              <div className="about-service-footer">
                <strong>Focus</strong>
                <span>Equipment Safety</span>
              </div>
            </article>

            <article className="about-service-card">
              <div className="about-service-icon">
                <FaCertificate />
              </div>

              <div className="about-service-content">
                <span>03 • CERTIFICATION</span>

                <h3>Professional Certification</h3>

                <p>
                  Certification processes that provide documented
                  evidence of professional competence and support
                  confidence in qualified personnel.
                </p>
              </div>

              <div className="about-service-footer">
                <strong>Focus</strong>
                <span>Professional Recognition</span>
              </div>
            </article>

            <article className="about-service-card">
              <div className="about-service-icon">
                <FaShieldAlt />
              </div>

              <div className="about-service-content">
                <span>04 • SAFETY</span>

                <h3>Safety &amp; Operational Support</h3>

                <p>
                  A safety-focused approach that brings together
                  people, competence, inspection, certification,
                  and responsible operational practices.
                </p>
              </div>

              <div className="about-service-footer">
                <strong>Focus</strong>
                <span>Safer Operations</span>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* =====================================================
          CERTIFICATION SHOWCASE
      ====================================================== */}
      <section
        id="credentials"
        className="about-certification-section"
      >
        <div className="about-container">
          <div className="about-certification-grid">

            {/* Left */}
            <div className="about-certification-info">
              <div>
                <div className="about-eyebrow">
                  <span></span>
                  Credentials &amp; Verification
                </div>

                <h2>
                  Professional Certification
                  <br />
                  You Can Verify
                </h2>
              </div>

              <p>
                Certification is an important part of building
                confidence in professional competence. Our
                certification process provides documented evidence
                of training and qualification.
              </p>

              <div className="about-cert-features">
                <div className="about-cert-feature">
                  <div>
                    <FaCheckCircle />
                  </div>

                  <section>
                    <strong>Documented Credentials</strong>
                    <p>
                      Professional certification provides a clear
                      record of completed qualification.
                    </p>
                  </section>
                </div>

                <div className="about-cert-feature">
                  <div>
                    <FaSearch />
                  </div>

                  <section>
                    <strong>Verification Support</strong>
                    <p>
                      Certification information can be presented
                      for verification where applicable.
                    </p>
                  </section>
                </div>

                <div className="about-cert-feature">
                  <div>
                    <FaShieldAlt />
                  </div>

                  <section>
                    <strong>Safety-Focused Process</strong>
                    <p>
                      Our credentials support a culture of
                      competence and responsible operations.
                    </p>
                  </section>
                </div>
              </div>
            </div>

            {/* Right Certificate */}
            <div className="about-certificate-display">
              <div className="about-certificate-top-line"></div>

              {loadingCert ? (
                <div className="about-cert-loading">
                  <div className="about-loading-spinner"></div>
                  <span>Loading certification...</span>
                </div>
              ) : certificationImage ? (
                <>
                  <div className="about-certificate-header">
                    <div className="about-certificate-brand">
                      <FaCertificate />

                      <div>
                        <strong>DOFCAN OILSERVE LIMITED</strong>
                        <span>
                          Professional Certification
                        </span>
                      </div>
                    </div>

                    <div className="about-valid-badge">
                      <FaCheckCircle />
                      CERTIFIED
                    </div>
                  </div>

                  <div className="about-certificate-image">
                    <img
                      src={certificationImage}
                      alt="Dofcan Oilserve certification"
                    />
                  </div>

                  <div className="about-certificate-footer">
                    <span>
                      <FaShieldAlt />
                      Authentic certification document
                    </span>

                    <button
                      type="button"
                      onClick={() => setShowCertModal(true)}
                    >
                      <FaExternalLinkAlt />
                      View Larger
                    </button>
                  </div>
                </>
              ) : (
                <div className="about-no-cert">
                  <FaCertificate />

                  <h3>Certification Information</h3>

                  <p>
                    Our certification information will appear here
                    when available.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CREDENTIAL / COMMITMENT STRIP
      ====================================================== */}
      <section className="about-credentials-strip" data-reveal>
        <div className="about-container">
          <div className="about-strip-title">
            <span>
              OUR PROFESSIONAL COMMITMENTS
            </span>
          </div>

          <div className="about-credentials-grid" data-reveal="stagger">
            <div className="about-credential-item">
              <div>
                <FaGraduationCap />
              </div>

              <section>
                <strong>Professional Training</strong>
                <span>Competence Development</span>
              </section>
            </div>

            <div className="about-credential-item">
              <div>
                <FaClipboardCheck />
              </div>

              <section>
                <strong>Equipment Inspection</strong>
                <span>Operational Assurance</span>
              </section>
            </div>

            <div className="about-credential-item">
              <div>
                <FaCertificate />
              </div>

              <section>
                <strong>Certification</strong>
                <span>Professional Recognition</span>
              </section>
            </div>

            <div className="about-credential-item">
              <div>
                <FaShieldAlt />
              </div>

              <section>
                <strong>Safety Commitment</strong>
                <span>Safer Operations</span>
              </section>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          MANAGEMENT STANDARDS
      ====================================================== */}
      <StandardsSection
        eyebrow="Management Standards"
        title="Three Principles Behind How We Operate"
        intro="Quality, environmental responsibility, and occupational health and safety guide how we train, inspect, and certify. Each principle is anchored to an internationally recognised management standard."
        descriptions={[
          'Consistent service delivery, documented processes, and continual improvement across our training, inspection, and certification work.',
          'Responsible operations that take account of environmental impact in how we plan and carry out work on site.',
          'Safer working practices, hazard awareness, and health and safety performance at the centre of every assignment.',
        ]}
        note="These standards describe the principles our management approach is built around and the commitments we hold ourselves to in day-to-day operations."
      />

      {/* =====================================================
          CTA
      ====================================================== */}
      <section className="about-cta-section" data-reveal>
        <div className="about-container">
          <div className="about-cta-grid">
            <div className="about-cta-copy">
              <div className="about-cta-eyebrow">
                <span></span>
                Dofcan Oilserve Limited
              </div>

              <h2>
                Building Competence.
                <br />
                Supporting Safer Operations.
              </h2>

              <p>
                Whether you need professional training,
                equipment inspection, or certification support,
                our focus remains on practical competence and
                operational safety.
              </p>
            </div>

            <div className="about-cta-actions">
              <button
                type="button"
                onClick={() => navigate('/services')}
                className="about-cta-primary"
              >
                Explore Our Services
                <FaArrowRight />
              </button>

              <button
                type="button"
                onClick={() => navigate('/contact')}
                className="about-cta-secondary"
              >
                Contact Our Team
                <FaCertificate />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          CERTIFICATION MODAL
      ====================================================== */}
      {showCertModal && certificationImage && (
        <div
          className="about-cert-modal"
          onClick={() => setShowCertModal(false)}
        >
          <div
            className="about-cert-modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="about-cert-modal-close"
              onClick={() => setShowCertModal(false)}
              aria-label="Close certification"
            >
              <FaTimes />
            </button>

            <img
              src={certificationImage}
              alt="Dofcan Oilserve certification full view"
            />
          </div>
        </div>
      )}
    </div>
  );
}